// ============================================================================
// MarketFeedFilters — отборы ленты мастера: категория, услуга, поиск,
// цена, рейтинг мастера, срочность, «только новые», ГЕО и РАССТОЯНИЕ.
//
// • Серверные отборы (категория/услуга/статус) уходят в ListTableData;
//   текстовый поиск, цена, рейтинг, срочность и гео применяются в памяти по
//   единым правилам lib/marketFeed.ts (те же, что у живых событий).
// • Отборы СОХРАНЯЮТСЯ для пользователя (settingsStore → user_settings) и
//   восстанавливаются при открытии ленты; «Сбросить всё» очищает их и
//   сохраняет пустой набор (следующее открытие не вернёт старые условия).
// • Сводка активных отборов — чипы с названиями (категория/услуга/гео),
//   каждый снимается отдельно.
//
// Гео-отбор (каноническая схема v122): страна/регион/город/район — ссылки на
// справочники ядра; расстояние — от центра (координаты мастера) с радиусом.
// Правила те же, что на сервере: пустое гео у заявки — неизвестное измерение,
// оно НЕ отсекается; отсекает явное несовпадение ссылок или выход за радиус.
// Точные координаты заявки скрыты до оплаченного взятия, поэтому расстояние
// вычисляется только для раскрытых заявок (остальные не отбрасываются).
// ============================================================================

import { useState } from 'react';
import {
  CheckCircle2,
  Filter,
  MapPin,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MarketCategory, MarketService } from '@/lib/market';
import {
  marketFeedActiveFiltersCount,
  marketFeedFiltersActive,
  type MarketFeedFilters as MarketFeedFilterValues,
} from '@/lib/marketFeed';
import {
  EMPTY_MARKET_GEO,
  marketGeoFilter,
  marketGeoFilterActive,
  type MarketGeoLabelMap,
  type MarketGeoValue,
} from '@/lib/marketGeo';
import { MarketGeoFields } from './MarketGeoFields';

interface MarketFeedFiltersProps {
  categories: MarketCategory[];
  services: MarketService[];
  filters: MarketFeedFilterValues;
  /** Названия выбранных гео-ссылок (для сводки активных отборов). */
  geoLabels?: MarketGeoLabelMap;
  /** «Только новые» относится к свободным заявкам; во вкладке «Мои» скрыт. */
  showOnlyNew?: boolean;
  /** Показать ограничения данных поставки (район/подтверждение телефона). */
  showDataLimitations?: boolean;
  total: number;
  loading: boolean;
  /** Изменить часть отборов (родитель сохраняет и перезагружает ленту). */
  onChange: (patch: Partial<MarketFeedFilterValues>) => void;
  /** Сбросить ВСЕ отборы (включая сохранённые). */
  onResetAll: () => void;
  onRefresh: () => void;
}

/** Подпись срочности по выбранному окну (часы → человеческий текст). */
function urgencyLabel(value: string): string {
  switch (value) {
    case '1':
      return 'за последний час';
    case '3':
      return 'за 3 часа';
    case '24':
      return 'за сутки';
    case '168':
      return 'за неделю';
    default:
      return value ? `за ${value} ч` : '';
  }
}

/** Краткая подпись гео-отбора (названия уровней + радиус/точка). */
function geoSummary(geo: MarketGeoValue, labels: MarketGeoLabelMap): string {
  const levels = [labels.country, labels.region, labels.city, labels.area]
    .filter((part): part is string => Boolean(part));
  // Ссылка есть, но название ещё не загрузилось — показываем уровень без имени.
  if (levels.length === 0) {
    const placeholders = [
      geo.countryId ? 'страна' : '',
      geo.regionId ? 'регион' : '',
      geo.cityId ? 'город' : '',
      geo.areaId ? 'район' : '',
    ].filter(Boolean);
    if (placeholders.length > 0) levels.push(placeholders.join('/'));
  }
  const parts: string[] = [];
  if (levels.length > 0) parts.push(levels.join(' / '));
  const radius = geo.radiusKm.trim();
  if (radius) parts.push(`${radius} км`);
  else if (geo.lat.trim() && geo.lon.trim()) parts.push('точка на карте');
  return parts.join(' · ');
}

interface ActiveChip {
  key: string;
  label: string;
  patch: Partial<MarketFeedFilterValues>;
}

export function MarketFeedFilters({
  categories,
  services,
  filters,
  geoLabels = {},
  showOnlyNew = true,
  showDataLimitations = false,
  total,
  loading,
  onChange,
  onResetAll,
  onRefresh,
}: MarketFeedFiltersProps) {
  const [geoOpen, setGeoOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const selectable = categories.filter((c) => !c.isGroup);
  const serviceOptions = filters.categoryId
    ? services.filter((service) => service.categoryId === filters.categoryId)
    : services;
  const geoActive = marketGeoFilterActive(marketGeoFilter(filters.geo));
  // Бейдж панели «Фильтры» — дополнительные условия; «Только новые» имеет
  // собственную кнопку и в бейдж не входит.
  const extraCount = marketFeedActiveFiltersCount(filters)
    - (showOnlyNew && filters.onlyNew ? 1 : 0);
  const anyActive = marketFeedFiltersActive(filters);

  const category = categories.find((c) => c.id === filters.categoryId);
  const service = services.find((s) => s.id === filters.serviceId);
  const priceChip = filters.priceMin && filters.priceMax
    ? `Цена: ${filters.priceMin}–${filters.priceMax}`
    : filters.priceMin
      ? `Цена: от ${filters.priceMin}`
      : filters.priceMax
        ? `Цена: до ${filters.priceMax}`
        : '';

  const chips: ActiveChip[] = [];
  if (filters.categoryId) {
    chips.push({
      key: 'category',
      label: `Категория: ${category?.name || 'выбрана'}`,
      patch: { categoryId: '', serviceId: '' },
    });
  }
  if (filters.serviceId) {
    chips.push({
      key: 'service',
      label: `Услуга: ${service?.name || 'выбрана'}`,
      patch: { serviceId: '' },
    });
  }
  if (filters.query.trim()) {
    chips.push({
      key: 'query',
      label: `Поиск: «${filters.query.trim()}»`,
      patch: { query: '' },
    });
  }
  if (priceChip) {
    chips.push({ key: 'price', label: priceChip, patch: { priceMin: '', priceMax: '' } });
  }
  if (filters.ratingMin) {
    chips.push({
      key: 'rating',
      label: `Рейтинг мастера ≥ ${filters.ratingMin}`,
      patch: { ratingMin: '' },
    });
  }
  if (filters.urgencyHours) {
    chips.push({
      key: 'urgency',
      label: `Срочные: ${urgencyLabel(filters.urgencyHours)}`,
      patch: { urgencyHours: '' },
    });
  }
  if (geoActive) {
    chips.push({
      key: 'geo',
      label: `Гео: ${geoSummary(filters.geo, geoLabels) || 'включено'}`,
      patch: { geo: { ...EMPTY_MARKET_GEO } },
    });
  }
  if (showOnlyNew && filters.onlyNew) {
    chips.push({ key: 'onlyNew', label: 'Только новые', patch: { onlyNew: false } });
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Поиск по заявкам</span>
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Search className="h-3.5 w-3.5" />
          </span>
          <input
            type="search"
            value={filters.query}
            onChange={(e) => onChange({ query: e.target.value })}
            placeholder="Поиск: номер, описание, адрес"
            className="h-10 w-full rounded-xl border bg-card pl-8 pr-8 text-sm outline-none focus:border-primary"
          />
          {filters.query && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => onChange({ query: '' })}
              aria-label="Очистить поиск"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </label>

        <Button
          type="button"
          variant="ghost"
          className="h-10 w-10 shrink-0 rounded-xl p-0"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Обновить ленту"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Категория услуги</span>
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
          </span>
          <select
            value={filters.categoryId}
            onChange={(e) => onChange({ categoryId: e.target.value, serviceId: '' })}
            className="h-10 w-full appearance-none rounded-xl border bg-card pl-8 pr-8 text-sm outline-none focus:border-primary"
          >
            <option value="">Все категории</option>
            {selectable.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>

        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Услуга</span>
          <select
            value={filters.serviceId}
            onChange={(e) => onChange({ serviceId: e.target.value })}
            disabled={serviceOptions.length === 0}
            title={serviceOptions.length === 0 ? 'В этой категории услуг нет' : 'Услуга'}
            className="h-10 w-full appearance-none rounded-xl border bg-card px-3 text-sm outline-none focus:border-primary disabled:opacity-50"
          >
            <option value="">
              {filters.categoryId ? 'Все услуги категории' : 'Все услуги'}
            </option>
            {serviceOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={moreOpen || extraCount > 0 ? 'outline' : 'ghost'}
          className="h-9 flex-1 justify-start rounded-xl px-3 text-xs"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
        >
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
          Фильтры
          {extraCount > 0 && (
            <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {extraCount}
            </span>
          )}
        </Button>

        <Button
          type="button"
          variant={filters.onlyNew ? 'default' : 'outline'}
          className="h-9 shrink-0 rounded-xl px-3 text-xs"
          onClick={() => onChange({ onlyNew: !filters.onlyNew })}
          aria-pressed={filters.onlyNew}
          hidden={!showOnlyNew}
        >
          <CheckCircle2 className="mr-1.5 h-4 w-4" />
          Только новые
        </Button>
      </div>

      {moreOpen && (
        <div className="space-y-3 rounded-2xl border bg-card p-3">
          <div>
            <p className="mb-1.5 text-[11px] font-medium">Стоимость взятия, ₽</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="decimal"
                min="0"
                value={filters.priceMin}
                onChange={(e) => onChange({ priceMin: e.target.value })}
                placeholder="от"
                aria-label="Цена от"
                className="h-9 w-full rounded-xl border bg-background px-2.5 text-sm outline-none focus:border-primary"
              />
              <span className="text-muted-foreground">—</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                value={filters.priceMax}
                onChange={(e) => onChange({ priceMax: e.target.value })}
                placeholder="до"
                aria-label="Цена до"
                className="h-9 w-full rounded-xl border bg-background px-2.5 text-sm outline-none focus:border-primary"
              />
            </div>
            <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
              Заявки без указанной стоимости при заданной границе не показываются.
            </p>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-medium">Рейтинг мастера</p>
            <select
              value={filters.ratingMin}
              onChange={(e) => onChange({ ratingMin: e.target.value })}
              className="h-9 w-full appearance-none rounded-xl border bg-background px-2.5 text-sm outline-none focus:border-primary"
            >
              <option value="">Любой рейтинг</option>
              <option value="3">от 3,0</option>
              <option value="3.5">от 3,5</option>
              <option value="4">от 4,0</option>
              <option value="4.5">от 4,5</option>
            </select>
            <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
              Заявки без мастера (рейтинг неизвестен) не отсекаются.
            </p>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-medium">Срочность</p>
            <select
              value={filters.urgencyHours}
              onChange={(e) => onChange({ urgencyHours: e.target.value })}
              className="h-9 w-full appearance-none rounded-xl border bg-background px-2.5 text-sm outline-none focus:border-primary"
            >
              <option value="">Любая срочность</option>
              <option value="1">За последний час</option>
              <option value="3">За 3 часа</option>
              <option value="24">За сутки</option>
              <option value="168">За неделю</option>
            </select>
            <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
              В поставке нет отдельного признака срочности — срочные определяются
              по времени публикации заявки.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={geoActive || geoOpen ? 'outline' : 'ghost'}
          className="h-9 flex-1 justify-start rounded-xl px-3 text-xs"
          onClick={() => setGeoOpen((v) => !v)}
          aria-expanded={geoOpen}
        >
          <MapPin className="mr-1.5 h-3.5 w-3.5" />
          Гео и расстояние
          {geoActive && (
            <span className="ml-1.5 max-w-[45%] truncate rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {geoSummary(filters.geo, geoLabels) || 'включено'}
            </span>
          )}
        </Button>
        {geoActive && (
          <Button
            type="button"
            variant="ghost"
            className="h-9 shrink-0 rounded-xl px-2 text-[11px]"
            onClick={() => onChange({ geo: { ...EMPTY_MARKET_GEO } })}
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Сбросить
          </Button>
        )}
      </div>

      {geoOpen && (
        <div className="rounded-2xl border bg-card p-3">
          <MarketGeoFields
            value={filters.geo}
            onChange={(geo: MarketGeoValue) => onChange({ geo })}
            dense
            radiusLabel="Расстояние, км"
            radiusHint="Заявки дальше радиуса не показываются (учитывается и радиус самой заявки)."
          />
          <p className="mt-2 text-[10px] leading-4 text-muted-foreground">
            Пустые гео-поля у заявки — неизвестное измерение: отбор их не отсекает.
            Точные координаты заявки открываются после взятия, поэтому расстояние
            показывается для раскрытых заявок.
          </p>
        </div>
      )}

      {anyActive && (
        <div className="rounded-2xl border border-dashed bg-card/50 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-medium text-muted-foreground">Активные отборы</span>
            <Button
              type="button"
              variant="ghost"
              className="h-7 rounded-lg px-2 text-[11px]"
              onClick={onResetAll}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Сбросить всё
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => onChange(chip.patch)}
                className="flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-1 text-[11px] transition-colors hover:border-primary/50"
                title="Снять отбор"
              >
                <span className="truncate">{chip.label}</span>
                <X className="h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-0.5 text-[11px] text-muted-foreground">
        <span>Сначала свежие</span>
        <span className="tabular-nums">
          {loading && total === 0
            ? 'Загрузка…'
            : anyActive
              ? `Показано: ${total}`
              : `Найдено: ${total}`}
        </span>
      </div>

      <p className="px-0.5 text-[10px] leading-4 text-muted-foreground/80">
        Отборы сохраняются автоматически для вашей учётной записи.
      </p>

      {showDataLimitations && (
        <p className="px-0.5 text-[10px] leading-4 text-muted-foreground/80">
          Признак подтверждения телефона приходит с сервера: если номер не подтверждён,
          карточка показывает это и взятие недоступно.
        </p>
      )}
    </section>
  );
}
